import { useEffect, useRef } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { Loader2 } from "lucide-react"

import { DocumentIcon } from "@/components/icons/DocumentIcon"
import { DocumentOverlay } from "@/components/overlays/DocumentOverlay"
import { useIntersectionObserver } from "@/plugins/hooks/useIntersectionObserver"

interface DocumentGridProps {
    documents: ContentObjectItem[]
    loading?: boolean
    loadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
    displayScore?: boolean
}
export function DocumentGrid({ documents, loading, loadMore, hasMore, loadingMore, displayScore = false }: Readonly<DocumentGridProps>) {
    const gridContainerRef = useRef<HTMLDivElement>(null)
    const loadMoreRef = useRef<HTMLDivElement>(null)

    const handleScroll = () => {
        if (!gridContainerRef.current || loading || loadingMore || !hasMore || !loadMore) {
            return
        }

        const { scrollTop, scrollHeight, clientHeight } = gridContainerRef.current
        const diff = scrollHeight - scrollTop - clientHeight

        if (diff < 100) {
            loadMore()
        }
    }

    useEffect(() => {
        const gridContainer = gridContainerRef.current
        if (gridContainer) {
            gridContainer.addEventListener("scroll", handleScroll)

            return () => {
                gridContainer.removeEventListener("scroll", handleScroll)
            }
        }
    }, [loading, loadingMore, hasMore])

    useIntersectionObserver(loadMoreRef, () => {
        loadMore?.()
    }, { deps: [documents.length] })

    return (
        <div className="flex flex-col h-full w-full">
            <div ref={gridContainerRef} className="flex flex-col h-full w-full overflow-auto">
                <div className="w-full gap-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 p-4">
                    {
                        (documents.length > 0) &&
                        documents.map((doc) => (
                            <div key={doc.id} className="col-span-1 h-fit">
                                <DocumentOverlay document={doc.id}>
                                    <DocumentIcon doc={doc} displayScore={displayScore} />
                                </DocumentOverlay>
                            </div>
                        ))
                    }
                    {
                        (documents.length === 0 && !loading) &&
                        <div className="col-span-full h-full flex items-center justify-center">
                            <div className="text-2xl text-gray-500">No documents found</div>
                        </div>
                    }
                </div>
                {
                    (loadingMore || loading) && (
                        <div className="w-full flex justify-center items-center p-4">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    )
                }
                {
                    hasMore && loadMore && !loadingMore && documents.length > 0 && (
                        <div ref={loadMoreRef} />
                    )
                }
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 text-sm text-muted-foreground">
                {
                    (loading)
                        ? (loadingMore ? " Loading more..." : "Loading documents...")
                        : (documents.length === 0)
                            ? "No documents found."
                            : <>{documents.length} document{documents.length <= 1 ? "" : "s"}.</>
                }
            </div>
        </div>
    )
}